import { documentDefinitions } from '../documents';
import { buildContractPayload } from './buildContractPayload';
import {
  normalizeCompromissoLoadedFormData,
  normalizeContratoLocacaoLoadedFormData,
  normalizeAutorizacaoLocacaoLoadedFormData,
  normalizeParceriaVendasLoadedFormData,
} from './compromissoFormDataNormalize';
import { normalizeReciboSimplesLoadedFormData } from './reciboSimplesFormDataNormalize';
import {
  buildCustomTemplateContractPayload,
  isCustomTemplateFillingType,
  mergeSavedDataIntoPersonalizedForm,
  normalizeLoadedPersonalTemplate,
} from '../pages/contract-generator/utils/personalizedTemplate';
import { getUserTemplateById } from '../services/templateService';

const toFrontendTemplateId = (backendTemplateKey) => {
  if (!backendTemplateKey || typeof backendTemplateKey !== 'string') {
    return null;
  }

  const normalized = backendTemplateKey.toLowerCase().replace(/-/g, '_');

  const map = {
    compromisso_compra_venda: 'compromisso-compra-venda',
    contrato_locacao_imovel: 'contrato-locacao-imovel',
    autorizacao_locacao_imovel: 'autorizacao-locacao-imovel',
    proposta_compra_imovel: 'proposta-compra-imovel',
    contrato_parceria_vendas: 'contrato-parceria-vendas',
    recibo_simples: 'recibo-simples',
    autorizacao_gleba_loteamento: 'autorizacao-gleba-loteamento',
    autorizacao_venda_imovel: 'autorizacao-venda-imovel',
  };

  const fromMap = map[normalized];
  if (fromMap) return fromMap;

  return normalized.replace(/_/g, '-');
};

const mapBackendDataToFormData = (backendData) => {
  if (!backendData || typeof backendData !== 'object') return {};

  const keyMap = {
    VENDEDORES: 'vendedores',
    COMPRADORES: 'compradores',
    LOCADORES: 'locadores',
    LOCATARIOS: 'locatarios',
    FIADORES: 'fiadores',
    TESTEMUNHAS: 'testemunhas',
    TIPO_IMOVEL: 'tipo_imovel',
    CLAUSULA_GARANTIA: 'clausula_garantia',
    TIPO_PAGAMENTO: 'tipo_pagamento',
    TIPO_COMISSAO: 'tipo_comissao',
    PROPRIETARIOS: 'proprietarios',
    INTERMEDIADOR: 'intermediador',
    CAPTANTES: 'captantes',
    INTERMEDIADORAS: 'intermediadores',
    INTERMEDIADORES: 'intermediadores',
    cidade_assinatura: 'CIDADE_ASSINATURA',
    data_assinatura: 'DATA_ASSINATURA',
  };

  return Object.entries(backendData).reduce((acc, [key, value]) => {
    const mappedKey = keyMap[key] ?? key;
    acc[mappedKey] = value;
    return acc;
  }, {});
};

const initializeFormData = (schema) => {
  const data = {};

  schema.forEach((section) => {
    if (section.repeatable) {
      data[section.groupKey] = [];
    } else if (section.type === 'conditional') {
      data[section.groupKey] = null;
    } else if (Array.isArray(section.fields)) {
      section.fields.forEach((field) => {
        if (field.default != null && field.id) {
          data[field.id] = field.default;
        }
      });
    }
  });

  return data;
};

function normalizeLoadedFormData(frontendTemplateId, mapped) {
  if (
    frontendTemplateId === 'compromisso-compra-venda' ||
    frontendTemplateId === 'proposta-compra-imovel'
  ) {
    return normalizeCompromissoLoadedFormData(mapped);
  }
  if (frontendTemplateId === 'contrato-parceria-vendas') {
    return normalizeParceriaVendasLoadedFormData(mapped);
  }
  if (frontendTemplateId === 'contrato-locacao-imovel') {
    return normalizeContratoLocacaoLoadedFormData(mapped);
  }
  if (
    frontendTemplateId === 'autorizacao-locacao-imovel' ||
    frontendTemplateId === 'autorizacao-gleba-loteamento' ||
    frontendTemplateId === 'autorizacao-venda-imovel'
  ) {
    return normalizeAutorizacaoLocacaoLoadedFormData(mapped);
  }
  if (frontendTemplateId === 'recibo-simples') {
    return normalizeReciboSimplesLoadedFormData(mapped);
  }
  return mapped;
}

/**
 * Builds the same /fill-document payload used by contract-generator from a stored document.
 */
export async function buildStoredDocumentContractPayload(doc) {
  if (!doc?.job_id) {
    throw new Error('Documento sem job_id.');
  }

  if (isCustomTemplateFillingType(doc)) {
    const templateId = doc.template_id ?? doc.templateId;
    if (!templateId) {
      throw new Error('Documento sem modelo associado.');
    }

    const templateResult = await getUserTemplateById(templateId);
    if (!templateResult.success) {
      throw new Error(
        templateResult.error || 'Erro ao carregar modelo do documento.'
      );
    }

    const loaded = normalizeLoadedPersonalTemplate(templateResult.data);
    const savedData =
      doc.data && typeof doc.data === 'object' ? doc.data : {};
    const formData = mergeSavedDataIntoPersonalizedForm(
      loaded?.formFields ?? [],
      savedData
    );

    const payload = buildCustomTemplateContractPayload({
      templateKey:
        loaded?.templateKey ?? doc.templateKey ?? doc.template_key,
      templateId,
      title: doc.title,
      formData,
      formFields: loaded?.formFields ?? [],
    });
    payload.job_id = doc.job_id;
    return payload;
  }

  const frontendTemplateId = toFrontendTemplateId(
    doc.templateKey || doc.template_key
  );
  const definition =
    frontendTemplateId && documentDefinitions[frontendTemplateId];

  if (!definition) {
    throw new Error('Tipo de documento não encontrado.');
  }

  const schema = definition.formSchema ?? [];
  let mapped = mapBackendDataToFormData(doc.data);
  mapped = normalizeLoadedFormData(frontendTemplateId, mapped);

  const formData = {
    ...initializeFormData(schema),
    ...mapped,
  };

  const payload = buildContractPayload({
    templateKey: frontendTemplateId,
    title: doc.title,
    formData,
    fillingType: 'standard',
    schema,
  });
  payload.job_id = doc.job_id;
  return payload;
}
